/**
 * Email API v1 client for Preact CSR.
 * Same-origin /email/v1 + session cookie + X-Tenant-Id / X-Person-Id.
 * Paths come from @tshmanager/email-shared builders (shared SoT with mobile).
 */

import { doFetch, ApiError, buildMarkOpenedPath } from '@tshmanager/email-shared'

export { ApiError }

let _tenantId = null
let _personId = null
let _base = null

/**
 * Same-origin email JSON API on the app host.
 * Mobile uses api.* /email/v1 (or /api/email/v1 locally) via its own base.
 */
export function emailApiBase() {
  if (_base) return _base
  _base = '/email/v1'
  return _base
}

export function configureEmailApi({ tenantId, personId, base } = {}) {
  if (tenantId != null) _tenantId = String(tenantId)
  if (personId != null) _personId = String(personId)
  if (base) _base = base
}

/**
 * Identity headers required by Api::Email::BaseController. Exported for the
 * requests that cannot go through doFetch — multipart uploads (TipTap image
 * paste) need FormData + credentials and keep using fetch. Also used by
 * third-party fetchers (bootstrap5-tags live autocomplete).
 */
export function emailApiHeaders(extra = {}) {
  const headers = { Accept: 'application/json', ...extra }
  if (_tenantId) headers['X-Tenant-Id'] = _tenantId
  if (_personId) headers['X-Person-Id'] = _personId
  return headers
}

function adapter(extraHeaders = {}) {
  return {
    baseUrl: emailApiBase(),
    credentials: 'include',
    async getHeaders() {
      return emailApiHeaders({
        'Content-Type': 'application/json',
        ...extraHeaders,
      })
    },
    onUnauthorized() {
      console.warn('[emailApi] unauthorized — session expired?')
    },
  }
}

function absolutize(pathOrUrl) {
  if (!pathOrUrl) return emailApiBase()
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl
  if (pathOrUrl.startsWith('/email/v1') || pathOrUrl.startsWith('/api/email/v1')) return pathOrUrl
  if (pathOrUrl.startsWith('/')) {
    return `${emailApiBase()}${pathOrUrl}`
  }
  return `${emailApiBase()}/${pathOrUrl}`
}

/** Convenience: shared builder with emailApiBase() as first arg. */
export function apiPath(builderFn, ...args) {
  return builderFn(emailApiBase(), ...args)
}

export async function apiGet(url, { headers } = {}) {
  try {
    return await doFetch(absolutize(url), 'GET', adapter(headers || {}))
  } catch (e) {
    if (e instanceof ApiError) throw new Error(`GET ${url} → ${e.status}`)
    throw e
  }
}

export async function apiPatch(url, body = {}) {
  try {
    return await doFetch(absolutize(url), 'PATCH', adapter(), body)
  } catch (e) {
    if (e instanceof ApiError) throw new Error(`PATCH ${url} → ${e.status}`)
    throw e
  }
}

export async function apiPost(url, body = {}) {
  try {
    return await doFetch(absolutize(url), 'POST', adapter(), body)
  } catch (e) {
    if (e instanceof ApiError) throw new Error(`POST ${url} → ${e.status}`)
    throw e
  }
}

/**
 * Multipart POST — compose sends `email[files][]` alongside the JSON-ish
 * fields, so the browser must own the Content-Type boundary. Resolves to
 * `{ ok, status, data }` instead of throwing so the caller can surface the
 * server's validation messages.
 */
export async function apiPostForm(url, formData) {
  const res = await fetch(absolutize(url), {
    method: 'POST',
    headers: emailApiHeaders(),
    credentials: 'include',
    body: formData,
  })
  let data = null
  try {
    data = await res.json()
  } catch (_) { /* empty or non-JSON body */ }
  return { ok: res.ok, status: res.status, data }
}

export async function apiDelete(url) {
  try {
    const result = await doFetch(absolutize(url), 'DELETE', adapter())
    return result === undefined ? null : result
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 204) return null
      throw new Error(`DELETE ${url} → ${e.status}`)
    }
    throw e
  }
}

/** Fire-and-forget mark-opened (never HTTP-cached). */
export function ackThreadOpened(emailId) {
  const url = buildMarkOpenedPath(emailApiBase(), Number(emailId))
  doFetch(url, 'PATCH', adapter(), {})
    .catch(err => console.error('[emailStore] ackThreadOpened failed:', err))
}

/** Normalize v1 thread detail → Preact web thread shape (nested items). */
export function normalizeThreadDetail(data) {
  if (!data) return data
  if (Array.isArray(data.items) && data.items[0]?.email) return data

  const thread = data.thread || data
  const rawItems = data.items || []
  const commentOwnerIds = new Map()
  rawItems.forEach(i => {
    if (i.type !== 'email') return
    const email = i.email || i
    ;(email.comments || []).forEach(c => {
      if (c?.id != null) commentOwnerIds.set(Number(c.id), email.id)
    })
  })

  const items = rawItems.map(i => {
    if (i.type === 'email') {
      if (i.email) return i
      const { type, at, ...email } = i
      return {
        type: 'email',
        at: at || email.sent_at || email.created_at,
        email,
        comment: null,
        draft: null,
      }
    }
    if (i.type === 'comment') {
      if (i.comment) return i
      const { type, at, ...comment } = i
      return {
        type: 'comment',
        at: at || comment.created_at,
        email: null,
        comment: normalizeComment(comment, commentOwnerIds.get(Number(comment.id)) ?? thread.id),
        draft: null,
      }
    }
    if (i.type === 'draft') {
      if (i.draft) return i
      const { type, at, ...draft } = i
      return {
        type: 'draft',
        at: at || draft.updated_at,
        email: null,
        comment: null,
        draft,
      }
    }
    return i
  })

  return {
    ...thread,
    items,
    infobar: data.infobar || thread.infobar,
  }
}

export function normalizeComment(comment, emailId = null) {
  if (!comment) return comment
  return {
    id: comment.id,
    body: comment.body,
    created_at: comment.created_at,
    person_name: comment.person_name || comment.person?.name,
    person_id: comment.person_id || comment.person?.id,
    avatar_url: comment.avatar_url || comment.person?.avatar_url || null,
    is_own: comment.is_own,
    email_id: comment.email_id ?? emailId,
  }
}

export function normalizeThreadsList(data) {
  const rows = data?.threads || data?.emails || []
  const meta = data?.meta || data?.pagination || {}
  return {
    emails: rows.map(normalizeListRow),
    meta: {
      page: meta.page || 1,
      next_page: meta.page && meta.total_pages && meta.page < meta.total_pages
        ? meta.page + 1
        : (meta.next_page ?? null),
      total_count: meta.total_count ?? rows.length,
      unread_count: meta.unread_count ?? 0,
      ...meta,
    },
  }
}

export function normalizeListRow(t) {
  if (!t) return t
  const from = t.from_address
  const fromName = typeof from === 'object'
    ? (from?.name || from?.email || from?.address || '')
    : (t.from_name || '')
  return {
    ...t,
    from_name: t.from_name || fromName,
    last_activity: t.last_activity || t.last_activity_at || t.sent_at,
    preview: t.preview || t.snippet || '',
    email_tag_ids: t.email_tag_ids || (t.tags || []).map(tag => tag.id),
  }
}
